// Home v2 — holdings table. Real portfolio/railgun tokens (see useHomeDashboard).
// Toolbar reuses the previous-version controls: the network filter (SelectNetwork),
// an asset-type dropdown (Tokens/NFTs/DeFi) and the Search input. `bodyOverride`
// lets the screen swap in the existing Collections / DeFiPositions lists.
import React, { useEffect, useRef, useState } from 'react'
import { type Control, useWatch } from 'react-hook-form'
import { Pressable, View } from 'react-native'

import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import PureSpinner from '@common/assets/svg/PureSpinner'
import SearchIcon from '@common/assets/svg/SearchIcon'
import Search from '@common/components/Search'
import TokenIcon from '@common/components/TokenIcon'
import RollingText from '@common/components/RollingText'
import Text from '@common/components/Text'
import TokensSkeleton from '@common/modules/dashboard/components/Tokens/TokensSkeleton'
import useTheme from '@common/hooks/useTheme'
import { TabType } from '@common/modules/dashboard/components/TabsAndSearch/Tabs/Tab/Tab'
import { Mode } from '@web/contexts/appModeContext'

import { AssetRowData } from '../useHomeDashboard'
import useHomeBreakpoints from '../useHomeBreakpoints'
import { getDeltaColor, getModeAccent, getModeAccentSoft, RADII } from '../tokens'
import AssetTypeDropdown from './AssetTypeDropdown'
import NetworkPill from './NetworkPill'
import { PlusIcon } from './HomeIcons'

type Props = {
  assets: AssetRowData[]
  isLoading: boolean
  mode: Mode
  selectedId: string | null
  onSelect: (id: string) => void
  onAddToken: () => void
  assetType: TabType
  onAssetTypeChange: (tab: TabType) => void
  searchControl: Control<{ search: string }, any>
  onSearchCleared: () => void
  /** When set (NFTs / DeFi tabs), replaces the token rows with this content. */
  bodyOverride?: React.ReactNode
  /** Private balance fetch failed (Railgun engine/DB error). Shows a retry
   * prompt instead of the misleading "no tokens" empty state. */
  privateError?: boolean
  /** Private cold-sync: scan finished but POI verification still running and
   * funds are pending-POI. Shows a "verifying" hint instead of "no tokens". */
  pendingVerification?: boolean
  onRetry?: () => void
}

const AssetRow = ({
  asset,
  selected,
  accent,
  accentSoft,
  onSelect
}: {
  asset: AssetRowData
  selected: boolean
  accent: string
  accentSoft: string
  onSelect: (id: string) => void
}) => {
  const { theme } = useTheme()
  const [hovered, setHovered] = useState(false)
  const isUp = (asset.deltaPct ?? 0) >= 0

  return (
    <Pressable
      onPress={() => onSelect(asset.id)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: RADII.sm,
        borderLeftWidth: selected ? 3 : 0,
        borderLeftColor: accent,
        backgroundColor: selected
          ? accentSoft
          : hovered
          ? (theme.quaternaryBackground as string)
          : 'transparent'
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
        <TokenIcon address={asset.address} chainId={asset.chainId} width={36} height={36} withNetworkIcon />
        <View style={{ minWidth: 0 }}>
          <Text fontSize={14} weight="semiBold" numberOfLines={1}>
            {asset.symbol}
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text fontSize={14} appearance="secondaryText" numberOfLines={1}>
              {asset.priceUSDFormatted}
            </Text>
            {asset.deltaPct != null && (
              <Text fontSize={14} weight="medium" color={getDeltaColor(theme, isUp)}>
                {isUp ? '+' : ''}
                {asset.deltaPct.toFixed(2)}%
              </Text>
            )}
          </View>
        </View>
      </View>
      <View style={{ alignItems: 'flex-end', marginLeft: 16 }}>
        <RollingText
          fontSize={14}
          weight="semiBold"
          value={asset.balanceFormatted}
        />
        <RollingText
          fontSize={14}
          appearance="secondaryText"
          value={asset.balanceUSDFormatted}
        />
        {asset.pendingPOIUsd > 0 && (
          <Text fontSize={11} weight="medium" color={accent} numberOfLines={1}>
            {`+${formatDecimals(asset.pendingPOIUsd, 'value')} pending`}
          </Text>
        )}
      </View>
    </Pressable>
  )
}

const AssetTable: React.FC<Props> = ({
  assets,
  isLoading,
  mode,
  selectedId,
  onSelect,
  onAddToken,
  assetType,
  onAssetTypeChange,
  searchControl,
  onSearchCleared,
  bodyOverride,
  privateError,
  pendingVerification,
  onRetry
}) => {
  const { theme } = useTheme()
  const { isMobile } = useHomeBreakpoints()
  const accent = getModeAccent(theme, mode)
  const accentSoft = getModeAccentSoft(theme, mode)

  const [isSearchVisible, setIsSearchVisible] = useState(false)
  const searchRef = useRef<any>(null)
  const searchButtonRef = useRef<any>(null)
  // Live search term — lets the empty state distinguish "no match" from
  // "no tokens at all" (and anchors the e2e search test).
  const searchTerm = (useWatch({ control: searchControl, name: 'search' }) || '').trim()

  // Close the search when clicking outside it (mirrors TabsAndSearch).
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const onButton = searchButtonRef.current?.contains?.(e.target as Node)
      const onSearch = searchRef.current?.contains?.(e.target as Node)
      if (!isSearchVisible || onButton || onSearch) return
      setIsSearchVisible(false)
    }
    window.addEventListener('mousedown', onClick, { passive: true })
    return () => window.removeEventListener('mousedown', onClick)
  }, [isSearchVisible])

  const showTokenBody = !bodyOverride

  return (
    <View
      style={{
        backgroundColor: theme.homeCardBackground as string,
        borderRadius: RADII.lg,
        padding: 16
      }}
    >
      {/* toolbar */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 16,
          zIndex: 20
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap', zIndex: 20 }}>
          <NetworkPill currentTab={assetType} />
          <AssetTypeDropdown value={assetType} onChange={onAssetTypeChange} compact={isMobile} />
          <Pressable
            ref={searchButtonRef}
            testID={`search-glass-icon-${assetType}`}
            onPress={() => setIsSearchVisible((v) => !v)}
            style={{
              width: isMobile ? 30 : 36,
              height: isMobile ? 30 : 36,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: RADII.pill,
              backgroundColor: isSearchVisible ? (theme.infoBackground as string) : (theme.homeControlBackground as string),
              borderWidth: 1,
              borderColor: (isSearchVisible ? theme.primary : theme.homeCardBorder) as string
            }}
          >
            <SearchIcon width={isMobile ? 14 : 18} height={isMobile ? 14 : 18} color={(isSearchVisible ? theme.primary : theme.secondaryText) as string} />
          </Pressable>
        </View>
        <Pressable onPress={onAddToken} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <PlusIcon size={16} color={theme.linkText as string} />
          <Text fontSize={14} weight="semiBold" color={theme.linkText as string}>
            Add token
          </Text>
        </Pressable>
      </View>

      {/* search row — full-width below the toolbar when toggled open */}
      {isSearchVisible && (
        <View ref={searchRef} style={{ marginBottom: 16, zIndex: 19 }}>
          <Search
            autoFocus
            control={searchControl}
            height={36}
            hasLeftIcon={false}
            placeholder="Search for tokens"
            onSearchCleared={() => {
              onSearchCleared()
              setIsSearchVisible(false)
            }}
          />
        </View>
      )}

      {/* body */}
      {showTokenBody ? (
        <>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 12, marginBottom: 8 }}>
            <Text fontSize={12} appearance="secondaryText">
              Total assets
            </Text>
            <Text fontSize={12} appearance="secondaryText">
              Amount/USD
            </Text>
          </View>
          {privateError && assets.length === 0 ? (
            <View style={{ paddingVertical: 24, alignItems: 'center', gap: 8 }}>
              <Text fontSize={14} weight="medium" style={{ textAlign: 'center' }}>
                Couldn't load your private balance.
              </Text>
              <Text
                fontSize={13}
                appearance="secondaryText"
                style={{ textAlign: 'center', maxWidth: 320 }}
              >
                The private engine failed to start. Retry, or reload the page if it keeps failing.
              </Text>
              {onRetry && (
                <Pressable
                  onPress={onRetry}
                  style={{
                    marginTop: 4,
                    paddingVertical: 8,
                    paddingHorizontal: 20,
                    borderRadius: RADII.pill,
                    backgroundColor: accentSoft,
                    borderWidth: 1,
                    borderColor: accent
                  }}
                >
                  <Text fontSize={14} weight="semiBold" color={accent}>
                    Retry
                  </Text>
                </Pressable>
              )}
            </View>
          ) : isLoading && assets.length === 0 && !searchTerm ? (
            <TokensSkeleton amount={4} withHeader={false} />
          ) : pendingVerification ? (
            <View style={{ paddingVertical: 24, alignItems: 'center', gap: 10 }}>
              <PureSpinner style={{ width: 22, height: 22 }} color={accent} />
              <Text fontSize={14} weight="medium" style={{ textAlign: 'center' }}>
                Verifying your private balance…
              </Text>
              <Text
                fontSize={13}
                appearance="secondaryText"
                style={{ textAlign: 'center', maxWidth: 320 }}
              >
                Incoming funds appear here once proof-of-innocence verification completes.
              </Text>
            </View>
          ) : assets.length === 0 ? (
            <View style={{ paddingVertical: 24, alignItems: 'center' }}>
              <Text testID="no-tokens-text" fontSize={14} weight="medium">
                {searchTerm
                  ? `No tokens match "${searchTerm}".`
                  : "You don't have any tokens yet."}
              </Text>
            </View>
          ) : (
            assets.map((asset) => (
              <AssetRow
                key={asset.id}
                asset={asset}
                selected={asset.id === selectedId}
                accent={accent}
                accentSoft={accentSoft}
                onSelect={onSelect}
              />
            ))
          )}
        </>
      ) : (
        bodyOverride
      )}
    </View>
  )
}

export default React.memo(AssetTable)
